import React, { useRef, useEffect, useState } from "react";

type Props = {
  name?: string;
  classNameMain: string;
  classNameImgDiv?: string;
  img: string;
  title?: string;
  message?: string;
  placeHolder?: string;
  fileVal?: File | any;
  setFileVal?: (file: File | null) => void;
  extraParentClasses?: string;
  register?: any;
  inputProps?: any;
  required?: boolean;
  error?: any;
  accept?: string;
  showPreview?: boolean;
  validation?: any;
};

const getFileNameFromUrl = (url: string): string => {
  return decodeURIComponent(url?.split("/")?.pop()?.split("?")[0] || "");
};

const FileUploader = ({
  name,
  classNameMain,
  classNameImgDiv,
  img,
  title,
  message,
  placeHolder,
  fileVal,
  setFileVal,
  extraParentClasses,
  required,
  validation,
  inputProps,
  register,
  error,
  accept,
  showPreview,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [objectUrl, setObjectUrl] = useState<string>("");

  useEffect(() => {
    if (fileVal instanceof File) {
      const url = URL.createObjectURL(fileVal);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (
      fileVal &&
      typeof fileVal === "object" &&
      "preview" in fileVal &&
      typeof fileVal.preview === "string"
    ) {
      setObjectUrl(fileVal.preview);
    } else {
      setObjectUrl("");
    }
  }, [fileVal]);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFileVal?.(file);

    if (register && name) {
      register(name, validation);
    }

    if (inputProps?.onChange) {
      inputProps.onChange(e);
    }
  };

  const openPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (objectUrl) {
      window.open(objectUrl, "_blank");
    }
  };

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFileVal?.(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`space-y-2 ${extraParentClasses || ""}`}>
      {title && (
        <label className="text-[#1E293B] text-sm ">
          {title}
          {required && <span className="text-[#DC3545] ml-1">*</span>}
        </label>
      )}

      <div
        className={`border ${
          error ? "border-[#DC3545]" : "border-[#d2d2d2]"
        } rounded-lg  w-full relative ${classNameMain} transition-colors cursor-pointer`}
      >
        <div
          className={`flex flex-col  items-center justify-center gap-2 w-full border border-dashed border-black/20 bg-white rounded-3xl p-2 flex-wrap ${classNameImgDiv}`}
        >
          <div className="w-14 h-14 flex-shrink-0 rounded-full flex  justify-center items-center mt-1 border bg-[#FBD037]/20 border-[#FBD037]">
            <img
              src={img}
              alt="Upload"
              className="object-contain size-8"
              onClick={handleImageClick}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "fallback-icon.svg";
              }}
            />
          </div>

          {!fileVal ? (
            <div className="flex-1 flex flex-col justify-center">
              <p className="text-sm text-center  text-[#212121]/70">
                {message || "Click to upload"}
              </p>
              <p className="text-xs text-center text-[#64748B] mt-1">
                {placeHolder}
              </p>
            </div>
          ) : (
            <div className="flex-1 w-full">
              <div className="flex justify-between items-start flex-wrap gap-2 px-2">
                <div className="flex flex-col justify-center">
                  {fileVal instanceof File && (
                    <>
                      <p className="text-sm font-medium text-[#212121] truncate max-w-80 break-all">
                        Selected file: {fileVal.name}
                      </p>
                      <p className="text-xs text-[#64748B] mt-1">
                        {(fileVal.size / 1024).toFixed(1)} KB
                      </p>
                    </>
                  )}
                </div>

                {showPreview && objectUrl && (
                  <>
                    <p className="text-sm font-medium text-[#212121] truncate max-w-[180px] break-all">
                      {getFileNameFromUrl(fileVal?.url)}
                    </p>
                    <div className="flex gap-2 mt-1">
                      <button
                        type="button"
                        onClick={openPreview}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors flex items-center gap-1"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3.5 w-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-1.5 text-gray-500 hover:text-red-500 transition-colors"
                        title="Remove file"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <input
          type="file"
          className="hidden"
          accept={accept}
          placeholder={placeHolder}
          onChange={handleFileChange}
          ref={fileInputRef}
        />
      </div>

      {error && (
        <span className="text-red-500 text-sm min-h-[0.5rem] block pl-3">
          {error.message}
        </span>
      )}
    </div>
  );
};

export default FileUploader;
